import { Icon, cn, type IconName } from "@sagot/ui";

export interface PillOption<T extends string> {
  value: T;
  label: string;
  icon?: IconName;
  /** Small muted suffix, e.g. "· auto". */
  suffix?: string;
}

interface PillChoiceBase<T extends string> {
  options: readonly PillOption<T>[];
  "aria-label"?: string;
  "aria-labelledby"?: string;
  size?: "sm" | "md";
}

type PillChoiceProps<T extends string> = PillChoiceBase<T> &
  ({ multiple?: false; value: T; onChange: (value: T) => void } | { multiple: true; value: readonly T[]; onChange: (value: T[]) => void });

/** `[data-choice-group]` pill toggles as drawn on the register / onboarding pages (aria-pressed). */
export function PillChoice<T extends string>(props: PillChoiceProps<T>) {
  const { options, size = "md" } = props;
  const pressed = (value: T) => (props.multiple ? props.value.includes(value) : props.value === value);
  const toggle = (value: T) => {
    if (!props.multiple) {
      props.onChange(value);
      return;
    }
    props.onChange(props.value.includes(value) ? props.value.filter((v) => v !== value) : [...props.value, value]);
  };
  return (
    <div role="group" aria-label={props["aria-label"]} aria-labelledby={props["aria-labelledby"]} className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={pressed(option.value)}
          onClick={() => {
            toggle(option.value);
          }}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-pill border border-line-strong bg-surface px-3 text-[13px] font-semibold text-fg-muted hover:border-primary aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-ink",
            size === "md" ? "h-9" : "h-8",
          )}
        >
          {option.icon ? <Icon name={option.icon} className="size-4" /> : null}
          {option.label}
          {option.suffix ? <span className="text-[11px] font-medium opacity-75">{option.suffix}</span> : null}
        </button>
      ))}
    </div>
  );
}
